import { CheckoutState } from './store'

export interface ValidationMessage {
   Text: string
   Step: 'information' | 'shipping' | 'payment'
}

export const informationValidationMessages = (state: CheckoutState) => {
   const messages: ValidationMessage[] = [...contactInformationMessages(state)]
   return messages
}

export const orderTypeValidationMessages = (state: CheckoutState) => {
   const messages: ValidationMessage[] = [...orderTypeMessages(state)]
   return messages
}

export const shippingValidationMessages = (state: CheckoutState) => {
   const messages: ValidationMessage[] = [...shippingAddressMessages(state)]
   return messages
}

export const paymentValidationMessages = (state: CheckoutState) => {
   const messages: ValidationMessage[] = [
      ...paymentMethodMessages(state),
      ...billingAddressMessages(state),
   ]
   return messages
}

export const allValidationMessages = (state: CheckoutState) => {
   const messages: ValidationMessage[] = [
      ...contactInformationMessages(state),
      ...orderTypeMessages(state),
      ...shippingAddressMessages(state),
      ...paymentMethodMessages(state),
      ...billingAddressMessages(state),
   ]
   return messages
}

const contactInformationMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = [...partyMessages(state), ...schoolMessages(state)]
   return messages
}

const partyMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = []
   if (!!state.modelData.PartyId) {
      return messages
   }
   if (state.accountType === 'existing') {
      messages.push({
         Text:
            'Contact Information - <a href="/Store/Cart/Login?destination=Checkout&logoutFirst=true">Log in</a> to your existing account',
         Step: 'information',
      })
      return messages
   }

   if (!state.purchase.EmailAddress) {
      messages.push({
         Text: 'Contact Information - You must enter an email address',
         Step: 'information',
      })
   }
   if (!state.purchase.FirstName || state.purchase.FirstName.length < 2) {
      messages.push({
         Text: 'Contact Information - You must enter a first name',
         Step: 'information',
      })
   }
   if (!state.purchase.LastName || state.purchase.LastName.length < 2) {
      messages.push({
         Text: 'Contact Information - You must enter a last name',
         Step: 'information',
      })
   }
   if (state.modelData.HasDigitalProducts && !state.purchase.Password) {
      messages.push({
         Text: 'Contact Information - You must choose a password',
         Step: 'information',
      })
   }

   if (!!state.purchase.Password)
   {
      let pwd = state.purchase.Password
      let minLen = state.modelData.PasswordMinLength
      let minUppers = state.modelData.PasswordMinUpper
      let minLowers = state.modelData.PasswordMinLower
      let minNumeric = state.modelData.PasswordMinNumeric
      let minPunctuation = state.modelData.PasswordMinPunctuation
      let punctuation = state.modelData.PasswordPunctuation
      if (pwd.length < minLen) {
         messages.push({
            Text: 'Contact Information - Passwords must be at least ' + minLen + ' characters',
            Step: 'information',
         })
      } else {
         const count = (str: string, exp: string) => {
            var re = new RegExp(exp, 'g')
            return ((str || '').match(re) || []).length
         }

         if (minUppers > 0 && count(pwd, '[A-Z]') < minUppers) {
            messages.push({
               Text: 'Contact Information - Passwords must have at least ' + minUppers + ' capital letter',
               Step: 'information',
            })
         }
         if (minLowers > 0 && count(pwd, '[a-z]') < minLowers) {
            messages.push({
               Text: 'Contact Information - Passwords must have at least ' + minLowers + ' lower case letter',
               Step: 'information',
            })
         }
         if (minNumeric > 0 && count(pwd, '[0-9]') < minNumeric) {
            messages.push({
               Text: 'Contact Information - Passwords must have at least ' + minNumeric + ' number',
               Step: 'information',
            })
         }
         if (punctuation.length > 0
            && minPunctuation > 0 && count(pwd, '[' + punctuation + ']') < minPunctuation) {
            messages.push({
               Text: 'Contact Information - Passwords must have at least ' + minPunctuation
                  + ' punctuation letter in set of ' + punctuation,
               Step: 'information',
            })
         }
      }
   }
   return messages
}

const schoolMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = []
   if (state.purchase.IsPersonalOrder) {
      return messages
   }
   if (!state.purchase.SchoolId) {
      messages.push({
         Text: 'Contact Information - You must choose your school to place a School Order',
         Step: 'information',
      })
   }
   return messages
}

const orderTypeMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = []
   return messages
}

const paymentMethodMessages = (state: CheckoutState): ValidationMessage[] => {
   if (state.purchase.IsSchoolInvoicing) {
      return schoolInvoicingMessages(state)
   } else {
      return creditCardMessages(state)
   }
}

const schoolInvoicingMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = []
   if (!state.purchase.SchoolId) {
      messages.push({
         Text: 'Payment Method Invoice my School - You must choose your school',
         Step: 'payment',
      })
   }
   if (!state.purchase.SchoolOrderNumber) {
      messages.push({
         Text: 'Payment Method Invoice my School - You must enter a purchase order number',
         Step: 'payment',
      })
   }
   return messages
}
const creditCardMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = []
   if (!!state.purchase.CreditCardId) {
      return messages
   }

   if (state.modelData.Totals.GrandTotal === 0) {
      return messages
   }

   if (!state.purchase.NameOnCard) {
      messages.push({
         Text: 'Payment Method Credit Card - Name on Card is missing',
         Step: 'payment',
      })
   }
   if (!state.purchase.CardNumber) {
      messages.push({
         Text: 'Payment Method Credit Card - Card Number is missing',
         Step: 'payment',
      })
   }
   if (!state.purchase.CardCvc) {
      messages.push({
         Text: 'Payment Method Credit Card - CVC is missing',
         Step: 'payment',
      })
   }
   if (state.purchase.ExpiryMonth <= 0) {
      messages.push({
         Text: 'Payment Method Credit Card - Expiry Month is missing',
         Step: 'payment',
      })
   }
   if (state.purchase.ExpiryYear <= 0) {
      messages.push({
         Text: 'Payment Method Credit Card - Expiry Year is missing',
         Step: 'payment',
      })
   }
   return messages
}

const shippingAddressMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = []

   if (!state.modelData.HasPhysicalProducts) {
      return messages
   }

   if (!!state.purchase.ShippingAddressId) {
      return messages
   }

   if (!state.purchase.IsPersonalOrder) {
      return messages
   }

   if (state.purchase.ClickAndCollect) {
      if (!state.purchase.ShippingAddress.ContactPhoneNumber) {
         messages.push({
            Text: 'Shipping Address - Contact Phone Number is missing',
            Step: 'shipping',
         })
      }
      return messages
   }

   if (!state.purchase.ShippingAddress) {
      messages.push({ Text: 'Shipping Address is missing', Step: 'shipping' })
   } else {
      if (!state.purchase.ShippingAddress.AddressLine1) {
         messages.push({ Text: 'Shipping Address is missing', Step: 'shipping' })
      }
      if (!state.purchase.ShippingAddress.Postcode) {
         messages.push({ Text: 'Shipping Address - Postcode is missing', Step: 'shipping' })
      }
      if (!state.purchase.ShippingAddress.ContactPhoneNumber) {
         messages.push({
            Text: 'Shipping Address - Contact Phone Number is missing',
            Step: 'shipping',
         })
      }
      if (!state.purchase.ShippingAddress.Country) {
         messages.push({ Text: 'Shipping Address - Country is missing', Step: 'shipping' })
      }
   }
   return messages
}

const billingAddressMessages = (state: CheckoutState): ValidationMessage[] => {
   const messages: ValidationMessage[] = []

   if (!state.requireBillingAddress) {
      return messages
   }

   if (!state.purchase.IsPersonalOrder) {
      return messages
   }

   if (
      state.modelData.HasPhysicalProducts &&
      state.purchase.BillingAddressIsSameAsShippingAddress
   ) {
      return messages
   }

   if (!!state.purchase.BillingAddressId) {
      return messages
   }

   if (!state.purchase.BillingAddress) {
      messages.push({ Text: 'Billing Address is missing', Step: 'payment' })
   } else {
      if (!state.purchase.BillingAddress.AddressLine1) {
         messages.push({ Text: 'Billing Address is missing', Step: 'payment' })
      }
      if (!state.purchase.BillingAddress.Postcode) {
         messages.push({ Text: 'Billing Address - Postcode is missing', Step: 'payment' })
      }
      if (!state.purchase.BillingAddress.ContactPhoneNumber) {
         messages.push({
            Text: 'Billing Address - Contact Phone Number is missing',
            Step: 'payment',
         })
      }
      if (!state.purchase.BillingAddress.Country) {
         messages.push({ Text: 'Billing Address - Country is missing', Step: 'payment' })
      }
   }
   return messages
}
